import { Component, OnDestroy } from '@angular/core';
import { TriggerAction } from '@shared/actions';
import { ButtonOutline } from '@shared/buttons/button-outline/button-outline.data';
import { ConfirmDialogService } from '@shared/confirm-dialog';
import {
  ConfirmDialogAnswer,
  ConfirmDialogResults,
  TextsForErrorsDialogs,
} from '@shared/confirm-dialog/confirm-dialog.data';
import { StateManagerProjectService } from '@state-manager/state-manager-project.service';
import { CornerstoneService } from '@viewer/cornerstone.service';
import { CornerstoneTool } from '@viewer/data';
import { OCCViewerService } from '@viewer/occ-viewer/occ-viewer.service';
import {
  ProjectActionsRequestService,
  TimeTrackerService,
} from '@viewer/services';
import { LogMessageAction } from '@viewer/services/log-event.data';
import { combineLatest, map, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-reject-action',
  templateUrl: 'reject-action.component.html',
  styleUrls: ['reject-action.component.scss'],
})
export class RejectActionComponent implements OnDestroy {
  readonly ButtonOutline = ButtonOutline;

  public rejectAction: TriggerAction;

  private _unsubscribe$ = new Subject();
  displaySpinner: boolean = false;

  constructor(
    private _confirmDialogService: ConfirmDialogService,
    public stateManagerProjectService: StateManagerProjectService,
    private _projectActionsRequestService: ProjectActionsRequestService,
    private _cornerstoneService: CornerstoneService,
    private _timeTrackerService: TimeTrackerService,
    private _confirmDialog: ConfirmDialogService,
    private _occViewerService: OCCViewerService
  ) {
    this.rejectAction = new TriggerAction('', 'Actions.REJECT', () => {
      this._confirmDialogService
        .openConfirmDialog(
          'Dialog.REJECT_FILE_MESSAGE',
          'Dialog.RejectButtons.BACK',
          'Dialog.ConfirmButtons.REJECT',
          true
        )
        .pipe(takeUntil(this._unsubscribe$))
        .subscribe((res: ConfirmDialogAnswer) => {
          if (res.result !== ConfirmDialogResults[1]) {
            return;
          }
          this.displaySpinner = true;
          this.stateManagerProjectService.isDisabledAllToolsForChangeMask =
            true;
          this._cornerstoneService.toolActivate(CornerstoneTool.EmptyTool);
          this.stateManagerProjectService.isRejectClicked = true;
          this._timeTrackerService.destroyObservable().subscribe((_) => {
            this._timeTrackerService.timeRefresh();

            if (this.stateManagerProjectService.isMaskWasChanged) {
              this._cornerstoneService
                .saveMask(undefined, false, () => {
                  this._occViewerService.callbackAfterLoadModel = () =>
                    this._rejectCase(res.data!);
                })
                .catch(() => this._showErrorMessage());
            } else {
              this._rejectCase(res.data!);
            }
          },(error)=>{
            this._showErrorMessage();
          });
        });
    });

    this.rejectAction.isEnable = () =>
      combineLatest([
        this.stateManagerProjectService.isViewerLoaded$,
        this.stateManagerProjectService.isDisabledAllToolsForChangeMask$,
      ]).pipe(
        map(
          ([isViewerLoaded, isDisabledAllToolsForChangeMask]) =>
            isViewerLoaded && !isDisabledAllToolsForChangeMask &&
            !this.stateManagerProjectService.isActiveShowChangesMode

        )
      );
  }

  private _showErrorMessage(): void {
    this.displaySpinner = false;
    this._confirmDialog
      .openConfirmDialog(
        TextsForErrorsDialogs.PROCESSING_ERROR_PLEASE_REFRESH,
        TextsForErrorsDialogs.CLOSE,
        ''
      )
      .subscribe((_) => {});
  }

  private _rejectCase(rejectMessage: string): void {
    this._projectActionsRequestService
      .requestRejectCaseStatus(
        this.stateManagerProjectService.caseId,
        rejectMessage,
        this.stateManagerProjectService.claimantUser?.claimantUserName,
        this.stateManagerProjectService.claimantUser?.claimantFirstName,
        this.stateManagerProjectService.claimantUser?.claimantLastName
      )
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe({
        next: () => {
          this._projectActionsRequestService
            .requestSaveLogForCase(
              this.stateManagerProjectService.caseId,
              this.stateManagerProjectService.currentUserName,
              LogMessageAction.CaseRejected,
              new Date().toISOString(),
              1,
              'UPDATE'
            )
            .subscribe({
              next: () => {
                this.displaySpinner = false;
                this._projectActionsRequestService.redirectToCaseList();
              },
              error: () => {
                this._showErrorMessage();
              },
            });
        },
        error: () => {
          this._showErrorMessage();
          this._timeTrackerService.init();
          this.stateManagerProjectService.isDisabledAllToolsForChangeMask =
            !this.stateManagerProjectService.isEditAllowed;
        },
      });
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next(undefined);
    this._unsubscribe$.complete();
  }
}
